(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-main-product';

  /* Minimum time the lightbox spinner stays up once shown, so a fast load reads
     as "loaded" rather than as a flicker. Not applied to already-cached images —
     those skip the spinner entirely. */
  var MIN_SPINNER_MS = 400;

  function initSection(section) {
    initGallery(section);
    initVariantPicker(section);
    initFrequencySelector(section);
    initAccordion(section);
    initAddToCart(section);
    initFlavorAjax(section);
    suppressRechargeWidget(section);
  }

  /* ---- Recharge widget ----
     The Recharge app embed hunts for form.shopify-product-form and prepends its
     own subscribe/one-time widget to it. In this section the form wraps the whole
     info column, so the widget landed above the reviews and title — and its
     radios plus a <select name="selling_plan"> competed with the frequency
     selector this section already renders, which drives [data-selling-plan-input]
     itself (see initFrequencySelector). So it is duplicate UI on a shared field.

     Hide it and disable its fields rather than removing the nodes, so Recharge's
     own script still finds what it expects. Disabled fields are not submitted.
     The legacy main-product section is untouched — there the Recharge block is a
     real theme app block sitting correctly between variant picker and buy
     buttons, and this only ever runs inside .oddit-section-main-product. */
  function suppressRechargeWidget(section) {
    var form = section.querySelector('form[id^="product-form-"]');
    if (!form || typeof MutationObserver === 'undefined') {
      neutralize(section);
      return;
    }

    neutralize(section);

    /* The embed injects asynchronously and can re-render on variant change. */
    var observer = new MutationObserver(function () {
      neutralize(section);
    });
    observer.observe(form, { childList: true, subtree: true });

    /* Stop watching once the page has settled — this is a cleanup pass, not a
       permanent subscription to every DOM change inside the form. */
    setTimeout(function () {
      observer.disconnect();
      neutralize(section);
    }, 10000);
  }

  function neutralize(section) {
    var widgets = section.querySelectorAll('.rc-container-wrapper, .rc_container_wrapper');
    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];
      widget.setAttribute('aria-hidden', 'true');
      widget.classList.add('oddit-rc-suppressed');

      var fields = widget.querySelectorAll('input, select, textarea, button');
      for (var j = 0; j < fields.length; j++) {
        fields[j].disabled = true;
      }
    }
  }

  /* ---- Gallery ----
     The main image swaps on thumbnail click, magnifies on hover, and opens a
     lightbox on click. The swap used to read data-image-src/srcset/alt off the
     thumbnail, but the section only ever emitted data-image-id — so every read
     was null and the main image never changed. The section now emits all three
     and this reads them; the two sides have to stay in step. */
  function initGallery(section) {
    var mainImageContainer = section.querySelector('[data-main-image]');
    var thumbs = section.querySelectorAll('[data-thumbnail]');
    if (!mainImageContainer) return;

    var activeIndex = 0;

    for (var i = 0; i < thumbs.length; i++) {
      thumbs[i].addEventListener('click', handleThumbClick);
    }

    initLightbox(section, getActiveIndex);

    function getActiveIndex() {
      return activeIndex;
    }

    function handleThumbClick() {
      var thumb = this;
      var mainImg = mainImageContainer.querySelector('img');
      if (!mainImg) return;

      var newSrc = thumb.getAttribute('data-image-src');
      var newSrcset = thumb.getAttribute('data-image-srcset');
      var newAlt = thumb.getAttribute('data-image-alt');
      var newIndex = parseInt(thumb.getAttribute('data-image-index'), 10);

      /* srcset wins over src whenever both are set, so it has to be replaced
         first — otherwise the browser keeps painting the previous candidate. */
      if (newSrcset) {
        mainImg.setAttribute('srcset', newSrcset);
      } else {
        mainImg.removeAttribute('srcset');
      }
      if (newSrc) mainImg.src = newSrc;
      if (newAlt != null) mainImg.alt = newAlt;
      if (!isNaN(newIndex)) activeIndex = newIndex;

      for (var j = 0; j < thumbs.length; j++) {
        thumbs[j].classList.remove('oddit-section-main-product__thumbnail--active');
        thumbs[j].setAttribute('aria-current', 'false');
      }
      thumb.classList.add('oddit-section-main-product__thumbnail--active');
      thumb.setAttribute('aria-current', 'true');

      warmNeighbours(activeIndex);
    }

    /* ---- ZAP-86: swipe the main image on touch ----
       The gallery is a single <img> swapped by thumbnail clicks, so there was
       nothing to swipe. Rather than rebuild it as a scroll track, a horizontal
       drag advances to the neighbouring thumbnail and reuses handleThumbClick,
       so selection, aria-current and the lightbox index all stay in step. */
    /* Decode the images either side of the current one ahead of time. The main
       image is a src swap, so without this every swipe waited on a network
       fetch before painting. Neighbours only — preloading all of them would
       compete with the rest of the page for bandwidth. */
    function warmNeighbours(index) {
      var offsets = [index - 1, index + 1];
      for (var w = 0; w < offsets.length; w++) {
        var t = thumbs[offsets[w]];
        if (!t) continue;
        var src = t.getAttribute('data-image-src');
        var srcset = t.getAttribute('data-image-srcset');
        if (!src && !srcset) continue;
        var pre = new Image();
        /* UM speed (2026-10-05): give the warm-up image the main image's sizes.
           Without it a detached Image() assumes 100vw, so a 3x phone pulled the
           1560 px file (about 350 KB) instead of the 780/1100 px one the main
           image actually shows, and the swap still missed the cache. */
        var mainForSizes = mainImageContainer.querySelector('img');
        if (mainForSizes && mainForSizes.getAttribute('sizes')) pre.sizes = mainForSizes.getAttribute('sizes');
        if (srcset) pre.srcset = srcset;
        if (src) pre.src = src;
      }
    }

    /* UM speed (2026-10-05): the first warm-up waits for the page's load event,
       so the neighbour photos no longer download alongside the main photo while
       the page is still painting. Thumbnail clicks and swipes still warm at once. */
    if (document.readyState === 'complete') {
      warmNeighbours(activeIndex);
    } else {
      window.addEventListener('load', function () { warmNeighbours(activeIndex); }, { once: true });
    }
    initSwipe();

    function initSwipe() {
      if (!mainImageContainer || thumbs.length < 2) return;

      var startX = 0;
      var startY = 0;
      var tracking = false;
      var didSwipe = false;
      var THRESHOLD = 40;

      /* Pointer events, not touch: the container is a <button>, and touchend
         did not reliably reach it. Pointer covers touch, pen and mouse, so the
         gesture is testable on desktop too. */
      mainImageContainer.addEventListener('pointerdown', function (e) {
        if (e.isPrimary === false) return;
        tracking = true;
        didSwipe = false;
        startX = e.clientX;
        startY = e.clientY;
        /* ZAP-86: capture the pointer so this element keeps receiving events
           even if the finger drifts off it mid-drag. Without capture, a swipe
           that ends outside the image never delivers pointerup here and the
           gesture is silently lost — which read as the swipe "not working"
           some of the time. */
        if (mainImageContainer.setPointerCapture) {
          try { mainImageContainer.setPointerCapture(e.pointerId); } catch (err) {}
        }
      });

      mainImageContainer.addEventListener('pointerup', function (e) {
        if (mainImageContainer.releasePointerCapture) {
          try { mainImageContainer.releasePointerCapture(e.pointerId); } catch (err) {}
        }
        if (!tracking) return;
        tracking = false;

        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        /* A mostly-vertical drag is the shopper scrolling the page. */
        if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

        var next = activeIndex + (dx < 0 ? 1 : -1);
        if (next < 0 || next >= thumbs.length) return;

        didSwipe = true;
        handleThumbClick.call(thumbs[next]);
        thumbs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });

      mainImageContainer.addEventListener('pointercancel', function (e) {
        if (mainImageContainer.releasePointerCapture) {
          try { mainImageContainer.releasePointerCapture(e.pointerId); } catch (err) {}
        }
        tracking = false;
      });

      /* A swipe ends in a click on the button, which would open the lightbox.
         Swallow that one click — captured so it never reaches the handler. */
      mainImageContainer.addEventListener('click', function (e) {
        if (!didSwipe) return;
        didSwipe = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);
    }
  }

  /* ---- Gallery: lightbox ---- */
  function initLightbox(section, getActiveIndex) {
    var lightbox = section.querySelector('[data-lightbox]');
    if (!lightbox) return;

    var slides = lightbox.querySelectorAll('[data-lightbox-slide]');
    var counter = lightbox.querySelector('[data-lightbox-counter]');
    var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
    var nextBtn = lightbox.querySelector('[data-lightbox-next]');
    var closeEls = lightbox.querySelectorAll('[data-lightbox-close]');
    var spinner = lightbox.querySelector('[data-lightbox-spinner]');
    var opener = section.querySelector('[data-open-lightbox]');
    if (!slides.length) return;

    var current = 0;
    var lastFocused = null;

    if (opener) {
      opener.addEventListener('click', function () {
        open(getActiveIndex ? getActiveIndex() : 0);
      });
    }

    for (var c = 0; c < closeEls.length; c++) {
      closeEls[c].addEventListener('click', close);
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    document.addEventListener('keydown', function (e) {
      if (lightbox.hidden) return;
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'ArrowLeft') {
        step(-1);
      } else if (e.key === 'ArrowRight') {
        step(1);
      }
    });

    function open(index) {
      lastFocused = document.activeElement;
      lightbox.hidden = false;
      /* Locking the body stops the page scrolling behind the overlay. */
      document.body.style.overflow = 'hidden';
      show(index || 0);
      var focusTarget = lightbox.querySelector('[data-lightbox-close]');
      if (focusTarget && focusTarget.focus) focusTarget.focus();
    }

    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function step(delta) {
      /* Wraps in both directions so the arrows never dead-end. */
      show((current + delta + slides.length) % slides.length);
    }

    function show(index) {
      current = index;
      for (var i = 0; i < slides.length; i++) {
        slides[i].classList.toggle('is-active', i === index);
      }
      if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;

      trackLoading(slides[index]);
      /* Warm the neighbours so stepping through feels instant after the first
         wait — they are lazy and display:none, so nothing fetches until asked. */
      preload(slides[(index + 1) % slides.length]);
      preload(slides[(index - 1 + slides.length) % slides.length]);
    }

    function trackLoading(slide) {
      /* Must come before the complete check: a lazy image inside display:none is
         never fetched, so without this the slide stays incomplete forever and
         the spinner never stops. */
      preload(slide);

      if (!spinner) return;

      if (!slide || slide.complete) {
        spinner.hidden = true;
        return;
      }

      spinner.hidden = false;
      var shownAt = Date.now();

      var done = function () {
        slide.removeEventListener('load', done);
        slide.removeEventListener('error', done);

        /* Hold it briefly. These images often return in tens of milliseconds, and
           a spinner that appears and vanishes inside one frame reads as a glitch
           rather than as loading. Below MIN_SPINNER_MS it never showed at all. */
        var elapsed = Date.now() - shownAt;
        var wait = Math.max(0, MIN_SPINNER_MS - elapsed);

        setTimeout(function () {
          /* Only clear if this is still the slide on screen — a fast
             click-through could otherwise let an older image's load event hide
             the spinner while the current one is still fetching. */
          if (slides[current] === slide) spinner.hidden = true;
        }, wait);
      };

      slide.addEventListener('load', done);
      slide.addEventListener('error', done);
    }

    function preload(slide) {
      if (slide && slide.loading === 'lazy') slide.loading = 'eager';
    }
  }

  /* ---- Variant picker ---- */
  function initVariantPicker(section) {
    var variantRadios = section.querySelectorAll('[data-variant-group] input[type=radio]');
    var variantIdInput = section.querySelector('[data-variant-id-input]');
    var variantsEl = section.querySelector('[data-product-variants]');

    if (!variantRadios.length || !variantIdInput || !variantsEl) return;

    var variants;
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (e) {
      return;
    }

    var optionGroups = section.querySelectorAll('[data-variant-group]');

    for (var i = 0; i < variantRadios.length; i++) {
      variantRadios[i].addEventListener('change', handleVariantChange);
    }

    updateAvailability();
    syncFlavorLinks();

    function groupPosition(group) {
      return parseInt(group.getAttribute('data-option-position'), 10);
    }

    function groupOf(el) {
      var node = el;
      while (node && node !== section) {
        if (node.getAttribute && node.hasAttribute('data-variant-group')) return node;
        node = node.parentNode;
      }
      return null;
    }

    function currentSelections() {
      var selections = [];
      for (var i = 0; i < optionGroups.length; i++) {
        var position = groupPosition(optionGroups[i]);
        var checked = optionGroups[i].querySelector('input[type=radio]:checked');
        if (!isNaN(position) && checked) {
          selections.push({ position: position, value: checked.value });
        }
      }
      return selections;
    }

    function matchVariant(selections) {
      for (var j = 0; j < variants.length; j++) {
        var v = variants[j];
        var isMatch = true;
        for (var k = 0; k < selections.length; k++) {
          if (v.options[selections[k].position - 1] !== selections[k].value) {
            isMatch = false;
            break;
          }
        }
        if (isMatch) return v;
      }
      return null;
    }

    function firstVariantFor(position, value) {
      var j;
      for (j = 0; j < variants.length; j++) {
        if (!variants[j].available) continue;
        if (position && variants[j].options[position - 1] !== value) continue;
        return variants[j];
      }
      if (position) {
        for (j = 0; j < variants.length; j++) {
          if (variants[j].options[position - 1] === value) return variants[j];
        }
      }
      return null;
    }

    function syncRadios(variant) {
      for (var i = 0; i < optionGroups.length; i++) {
        var position = groupPosition(optionGroups[i]);
        if (isNaN(position)) continue;
        var wanted = variant.options[position - 1];
        var radios = optionGroups[i].querySelectorAll('input[type=radio]');
        for (var r = 0; r < radios.length; r++) {
          radios[r].checked = radios[r].value === wanted;
        }
      }
    }

    function updateAvailability() {
      var selections = currentSelections();
      for (var i = 0; i < optionGroups.length; i++) {
        var position = groupPosition(optionGroups[i]);
        if (isNaN(position)) continue;
        var cards = optionGroups[i].querySelectorAll('[data-variant-card]');
        for (var c = 0; c < cards.length; c++) {
          var value = cards[c].getAttribute('data-option-value');
          var probe = [];
          for (var s = 0; s < selections.length; s++) {
            probe.push(
              selections[s].position === position
                ? { position: position, value: value }
                : selections[s]
            );
          }
          var candidate = matchVariant(probe);
          var unavailable = !candidate || !candidate.available;
          if (unavailable) {
            cards[c].classList.add('oddit-section-main-product__variant-card--unavailable');
          } else {
            cards[c].classList.remove('oddit-section-main-product__variant-card--unavailable');
          }
        }
      }
    }

    function syncFlavorLinks() {
      var links = section.querySelectorAll('[data-flavor-link]');
      if (!links.length) return;

      var sizeGroup = section.querySelector('[data-variant-group][data-option-role="size"]');
      var checked = sizeGroup && sizeGroup.querySelector('input[type=radio]:checked');

      for (var i = 0; i < links.length; i++) {
        var base = links[i].getAttribute('data-base-url');
        if (!base) continue;

        var sizeVariants = {};
        try {
          sizeVariants = JSON.parse(links[i].getAttribute('data-size-variants') || '{}');
        } catch (e) {
          sizeVariants = {};
        }

        var params = [];
        var variantId = checked ? sizeVariants[checked.value] : null;
        if (variantId) params.push('variant=' + variantId);

        var view = links[i].getAttribute('data-view');
        if (view) params.push('view=' + encodeURIComponent(view));

        links[i].setAttribute('href', params.length ? base + '?' + params.join('&') : base);
      }
    }

    /* The traits line names the pack ("10 Pack ⚡ 5 cal ⚡ ..."), so it has to
       follow the size. Every size's line is already in the DOM; this only picks
       which one is shown. Sizes with no line of their own fall back to the
       block's default line. */
    function syncTraits() {
      var lines = section.querySelectorAll('[data-traits-for]');
      var fallback = section.querySelector('[data-traits-default]');
      if (!lines.length) return;

      var sizeGroup = section.querySelector('[data-option-role="size"]');
      var checked = sizeGroup ? sizeGroup.querySelector('input[type=radio]:checked') : null;
      var sizeValue = checked ? checked.value : null;

      var matched = false;
      for (var i = 0; i < lines.length; i++) {
        var isMatch = sizeValue !== null && lines[i].getAttribute('data-traits-for') === sizeValue;
        lines[i].hidden = !isMatch;
        if (isMatch) matched = true;
      }

      if (fallback) fallback.hidden = matched;
    }

    function handleVariantChange(event) {
      var matched = matchVariant(currentSelections());

      if (!matched) {
        var changedGroup = event && event.target ? groupOf(event.target) : null;
        var changedPosition = changedGroup ? groupPosition(changedGroup) : null;
        if (isNaN(changedPosition)) changedPosition = null;

        matched = firstVariantFor(changedPosition, event && event.target ? event.target.value : null);
        if (!matched) matched = firstVariantFor(null, null);
        if (matched) syncRadios(matched);
      }

      if (!matched) return;

      updateAvailability();
      syncFlavorLinks();
      syncTraits();

      variantIdInput.value = matched.id;

      /* Update displayed price */
      var currentPriceEl = section.querySelector('[data-current-price]');
      var comparePriceEl = section.querySelector('[data-compare-price]');
      var saveBadgeEl = section.querySelector('[data-save-badge]');

      if (currentPriceEl) currentPriceEl.textContent = formatMoney(matched.price);

      if (comparePriceEl) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          comparePriceEl.textContent = formatMoney(matched.compare_at_price);
          comparePriceEl.style.display = '';
          if (saveBadgeEl) {
            var pct = Math.round((matched.compare_at_price - matched.price) / matched.compare_at_price * 100);
            saveBadgeEl.textContent = 'SAVE ' + pct + '%';
            saveBadgeEl.style.display = '';
          }
        } else {
          comparePriceEl.style.display = 'none';
          if (saveBadgeEl) saveBadgeEl.style.display = 'none';
        }
      }

      /* Update button availability */
      var addBtn = section.querySelector('[data-add-to-cart]');
      if (addBtn) {
        var label = addBtn.querySelector('[data-button-label]');
        addBtn.disabled = !matched.available;
        if (label) {
          label.textContent = matched.available
            ? (addBtn.getAttribute('data-add-text') || 'ADD TO CART')
            : 'SOLD OUT';
        }
      }

      /* Swap main image if variant has a featured image */
      if (matched.featured_image) {
        var mainImg = section.querySelector('[data-main-image] img');
        if (mainImg) {
          var base = matched.featured_image.src;
          mainImg.src = getSizedImageUrl(base, 1560);
          /* UM speed (2026-10-05): 1100w added, same as the section's srcset, so a
             3x phone switching pack size gets the ~200 KB file, not the ~420 KB one. */
          mainImg.setAttribute('srcset',
            getSizedImageUrl(base, 780) + ' 780w, ' +
            getSizedImageUrl(base, 1100) + ' 1100w, ' +
            getSizedImageUrl(base, 1560) + ' 1560w'
          );
          mainImg.alt = matched.featured_image.alt || '';
        }

        /* Highlight corresponding thumbnail */
        var thumbs = section.querySelectorAll('[data-thumbnail]');
        for (var t = 0; t < thumbs.length; t++) {
          var tid = thumbs[t].getAttribute('data-image-id');
          var isActive = tid && Number(tid) === matched.featured_image.id;
          thumbs[t].classList.toggle('oddit-section-main-product__thumbnail--active', isActive);
          thumbs[t].setAttribute('aria-current', isActive ? 'true' : 'false');
        }
      }

      /* Update URL without page reload */
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', matched.id);
        /* Keep whatever state is already on this entry — the flavour AJAX
           marks its entries, and replacing it with {} would strip that marker
           and leave back/forward unable to tell its own entries apart. */
        window.history.replaceState(window.history.state || {}, '', url.toString());
      }

      /* Update frequency prices for the new variant */
      updateFrequencyPrices(section, matched);
    }
  }

  function updateFrequencyPrices(section, variant) {
    var compareEl = section.querySelector('.oddit-section-main-product__frequency-compare');
    var onetimePriceEl = section.querySelector('.oddit-section-main-product__frequency-onetime-price');

    if (compareEl) compareEl.textContent = formatMoney(variant.price);
    if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(variant.price);

    /* The subscribe price was never refreshed, so switching pack size left the
       previous variant's discounted figure on screen while the compare and
       one-time prices moved — the subscription row showed a stale price.
       per_delivery_price already has the plan's discount applied. */
    var subPriceEl = section.querySelector('.oddit-section-main-product__frequency-current');
    if (subPriceEl) {
      var alloc = variant.selling_plan_allocations && variant.selling_plan_allocations[0];
      if (alloc && typeof alloc.per_delivery_price === 'number') {
        subPriceEl.textContent = formatMoney(alloc.per_delivery_price);
      }
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function getSizedImageUrl(src, size) {
    if (!src) return '';
    try {
      var url = new URL(src.indexOf('//') === 0 ? 'https:' + src : src);
      url.searchParams.set('width', String(size));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  /* ---- Frequency selector ---- */
  function initFrequencySelector(section) {
    var options = section.querySelectorAll('[data-frequency-option]');
    var sellingPlanInput = section.querySelector('[data-selling-plan-input]');
    if (!options.length) return;

    /* Set initial selling plan value to match default checked state */
    syncSellingPlan();

    for (var i = 0; i < options.length; i++) {
      options[i].addEventListener('change', handleFrequencyChange);
    }

    function handleFrequencyChange() {
      syncSellingPlan();
      for (var j = 0; j < options.length; j++) {
        var radio = options[j].querySelector('input[type=radio]');
        if (radio && radio.checked) {
          options[j].classList.add('oddit-section-main-product__frequency-option--selected');
        } else {
          options[j].classList.remove('oddit-section-main-product__frequency-option--selected');
        }
      }
    }

    function syncSellingPlan() {
      if (!sellingPlanInput) return;
      for (var k = 0; k < options.length; k++) {
        var radio = options[k].querySelector('input[type=radio]:checked');
        if (radio) {
          sellingPlanInput.value = radio.value === 'subscribe'
            ? (radio.getAttribute('data-selling-plan-id') || '')
            : '';
          break;
        }
      }
    }
  }

  /* ---- Accordion ---- */
  function initAccordion(section) {
    var headers = section.querySelectorAll('[data-accordion-header]');
    if (!headers.length) return;

    for (var i = 0; i < headers.length; i++) {
      headers[i].addEventListener('click', handleAccordionToggle);
    }

    function handleAccordionToggle() {
      var header = this;
      var content = header.nextElementSibling;
      if (!content) return;

      var isExpanded = header.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        content.style.maxHeight = content.scrollHeight + 'px';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            content.style.maxHeight = '0';
          });
        });
        header.setAttribute('aria-expanded', 'false');
      } else {
        content.style.maxHeight = content.scrollHeight + 'px';
        content.addEventListener('transitionend', function handler() {
          if (header.getAttribute('aria-expanded') === 'true') {
            content.style.maxHeight = 'none';
          }
          content.removeEventListener('transitionend', handler);
        });
        header.setAttribute('aria-expanded', 'true');
      }
    }
  }

  /* ---- Add to cart ---- */
  function initAddToCart(section) {
    var form = section.querySelector('form[id^=product-form-]');
    if (!form) return;

    form.addEventListener('submit', handleFormSubmit);

    function handleFormSubmit(e) {
      var btn = form.querySelector('[data-add-to-cart]');
      if (!btn || btn.disabled) return;

      /* If OdditCartDrawer is not available, let the form submit natively */
      if (!window.OdditCartDrawer) return;

      e.preventDefault();

      var restWidth = btn.getBoundingClientRect().width;
      btn.style.minWidth = restWidth + 'px';
      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      var formData = new FormData(form);
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        body: formData
      })
        .then(function (response) {
          /* The reason for a rejected add is in the body, not the status. */
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(
                (data && (data.description || data.message)) || 'Add to cart failed'
              );
            }
            return data;
          });
        })
        .then(function () {
          /* Opening alone showed the drawer with its pre-add contents and left
             the header count stale — refreshCart() redraws it and fires
             cart:updated, which is what the count badges listen for. */
          if (window.OdditCartDrawer.refreshCart) {
            return window.OdditCartDrawer.refreshCart().then(function () {
              window.OdditCartDrawer.open();
            });
          }
          window.OdditCartDrawer.open();
        })
        .catch(function (err) {
          console.error('[oddit-section-main-product]', err);
        })
        .finally(function () {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          btn.style.minWidth = '';
        });
    }
  }

  /* ---- AJAX flavour switching ----
     Every flavour is its own product, so a flavour pill is a real link to that
     product and following it reloads the whole page — throwing away the gallery,
     the scroll position and the Recharge embed for what reads to a shopper as a
     variant change. Intercept the click and swap just this section instead.

     The pills stay real <a href> elements, so middle-click, ctrl-click, "open in
     new tab" and a no-JS visitor all still navigate normally; and any failure
     falls back to a full navigation rather than stranding the shopper. */

  /* Params that pin a request to a specific theme. A flavour pill is a plain
     relative link, so following one from a theme preview drops these and lands
     the shopper on the *live* theme — where this template may not exist yet,
     which renders as a blank product area. Carry them across. */
  var THEME_CONTEXT_PARAMS = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb'];

  function withThemeContext(url) {
    try {
      var target = new URL(url, window.location.href);
      var current = new URLSearchParams(window.location.search);
      for (var i = 0; i < THEME_CONTEXT_PARAMS.length; i++) {
        var key = THEME_CONTEXT_PARAMS[i];
        if (current.has(key) && !target.searchParams.has(key)) {
          target.searchParams.set(key, current.get(key));
        }
      }
      return target;
    } catch (err) {
      return null;
    }
  }

  function initFlavorAjax(section) {
    /* The theme editor renders the preview inside its own frame and intercepts
       link clicks to keep that context alive. Swapping the section — or
       navigating programmatically — fights that and lands on a contextless,
       blank preview. In design mode the pills stay ordinary links and the
       editor keeps doing its job. */
    if (window.Shopify && window.Shopify.designMode) return;

    var links = section.querySelectorAll('[data-flavor-link]');
    for (var i = 0; i < links.length; i++) {
      /* Rewrite the href itself, not just the fetch, so every route to this
         flavour keeps the theme: the AJAX swap, the fallback navigation, a
         middle-click and "open in new tab" all stay on the same theme. */
      var ctx = withThemeContext(links[i].getAttribute('href'));
      if (ctx) links[i].setAttribute('href', ctx.pathname + ctx.search);

      links[i].addEventListener('click', handleFlavorClick);
    }
  }

  function handleFlavorClick(e) {
    /* Anything that means "open this somewhere else" is left to the browser. */
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.currentTarget;
    var url = link.getAttribute('href');
    if (!url) return;

    /* The current flavour is already on screen; don't refetch it. */
    if (link.getAttribute('aria-current') === 'page') {
      e.preventDefault();
      return;
    }

    e.preventDefault();

    /* Mark the entry we're leaving, so popstate can recognise its own history
       entries when the shopper comes back to it. */
    if (window.history.replaceState && !(window.history.state || {}).odditFlavor) {
      window.history.replaceState({ odditFlavor: true }, '', window.location.href);
    }

    swapFlavor(url, true, link.closest ? link.closest(SECTION_SELECTOR) : null);
  }

  /* The scroll-reveal animations (.oddit-reveal) start at opacity 0 and are
     switched on by an IntersectionObserver in oddit-interactions.js, which only
     ever observes the nodes that existed when it ran. Markup swapped in later is
     never observed, so it keeps opacity 0 forever: the product is present and
     still clickable — the gallery lightbox opens from the "empty" area — but
     nothing is drawn. Reveal it outright rather than re-running the observer;
     content that replaces what the shopper is already looking at should simply
     be there, not fade up from below as though it had just scrolled in. */
  function revealSwappedContent(root) {
    if (root.classList.contains('oddit-reveal')) {
      root.classList.add('is-visible');
    }
    var hidden = root.querySelectorAll('.oddit-reveal:not(.is-visible)');
    for (var i = 0; i < hidden.length; i++) {
      hidden[i].classList.add('is-visible');
    }

    /* The .shopify-section shell is opacity 0 until it is marked active. The
       swap replaces only the inner section, so the shell keeps the class it was
       given on load — but assert it anyway, so this cannot go dark if the shell
       is ever the thing that gets replaced. */
    var shell = root.closest ? root.closest('.shopify-section') : null;
    if (shell) shell.classList.add('oddit-section-active');
  }

  function swapFlavor(url, push, section) {
    section = section || document.querySelector(SECTION_SELECTOR);

    /* No section to swap, or a browser without what this needs: just navigate. */
    if (!section || typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') {
      window.location.href = url;
      return;
    }

    /* Ask for this section alone rather than the whole document. A full-page
       fetch can be 301'd to the shop's primary domain, which drops the theme
       context and answers with a page that has no section to lift out — the
       swap then finds nothing and the shopper is bounced to a blank preview.
       The Section Rendering API answers in the current theme and template, and
       returns markup instead of a whole page. */
    var endpoint = withThemeContext(url);
    if (!endpoint) {
      window.location.href = url;
      return;
    }
    if (section.id) endpoint.searchParams.set('section_id', section.id);

    section.classList.add('is-flavor-loading');
    section.setAttribute('aria-busy', 'true');

    window
      .fetch(endpoint.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector(SECTION_SELECTOR);

        /* A response that parses but carries no rendered product — the wrong
           template context, a 404 body, a bot/app interstitial — must never be
           swapped in: that is exactly what leaves a blank product area with the
           rest of the page intact. The container is the section's first real
           child, so its absence means this is not a rendered product section. */
        if (!next || !next.querySelector('.oddit-section-main-product__container')) {
          throw new Error('empty or unexpected section in response');
        }
        if (!section.parentNode) throw new Error('section detached');

        section.parentNode.replaceChild(next, section);
        initSection(next);
        revealSwappedContent(next);

        var title = doc.querySelector('title');
        if (title) document.title = title.textContent;

        if (push && window.history.pushState) {
          window.history.pushState({ odditFlavor: true }, '', url);
        }

        /* Let the rest of the theme know the product on screen changed. */
        if (typeof window.CustomEvent === 'function') {
          document.dispatchEvent(
            new CustomEvent('oddit:flavor-swapped', { detail: { url: url } })
          );
        }
      })
      .catch(function () {
        /* Never strand the shopper on a half-swapped page. */
        window.location.href = url;
      });
  }

  /* Back/forward between flavours. Only entries this script created are handled;
     anything else is a normal document navigation the browser already owns. */
  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.odditFlavor) return;
    if (!document.querySelector(SECTION_SELECTOR)) return;
    swapFlavor(window.location.href, false);
  });

  /* ---- Init ---- */
  var sections = document.querySelectorAll(SECTION_SELECTOR);
  for (var i = 0; i < sections.length; i++) {
    initSection(sections[i]);
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (!section) return;
    /* DOM-attached listeners are garbage-collected when the section element
       is removed. Add cleanup for timers or global state here if needed. */
  });
})();
